import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import formService from '../services/formService';
import standardsService from '../services/standardsService';
import { DYNAMIC_DROPDOWN_FIELDS } from '../config/dynamicDropdownConfig';
import PhotoFieldUpload from '../components/PhotoFieldUpload';
import DynamicRowsField from '../components/DynamicRowsField';

const EditSubmission = () => {
  const navigate = useNavigate();
  const { submissionId } = useParams();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [clients, setClients] = useState([]);
  const [formData, setFormData] = useState({});
  // Tracks which option INDEX is displayed as selected per select-type field. Needed because some
  // fields (e.g. "summary") have multiple options sharing identical text/value — a plain HTML
  // <select> always visually highlights the first matching option in that case, so we key options
  // by index and keep this map to show the actual chosen option regardless of duplicate text.
  const [selectDisplayIndex, setSelectDisplayIndex] = useState({});
  const [selectedClientId, setSelectedClientId] = useState('');
  const [inspectionDate, setInspectionDate] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [dynamicOptions, setDynamicOptions] = useState({});

  useEffect(() => {
    if (!submissionId) {
      navigate('/dashboard/jr_inspector');
      return;
    }

    fetchSubmissionData();
  }, [submissionId]);

  const fetchSubmissionData = async () => {
    try {
      setLoading(true);
      setError('');

      // Fetch submission details, template fields, clients, and standards options
      const [submissionResponse, clientsResponse, standardsResponse] = await Promise.all([
        formService.getSubmissionDetails(submissionId),
        formService.getClients(),
        standardsService.getAllStandards()
      ]);

      if (standardsResponse && standardsResponse.success) {
        setDynamicOptions(standardsResponse);
      }

      if (!submissionResponse.success) {
        throw new Error(submissionResponse.message || 'Failed to load submission');
      }

      const submissionData = submissionResponse.submission;

      // Check if this submission can be edited (must be sent_back_by_senior)
      if (submissionData.current_status !== 'sent_back_by_senior') {
        setError('This submission cannot be edited. Only forms sent back by reviewer can be edited.');
        setLoading(false);
        return;
      }

      // Check if user owns this submission
      if (submissionData.submitted_by !== user?.userid) {
        setError('You do not have permission to edit this submission.');
        setLoading(false);
        return;
      }

      // Get senior inspector comments from status history
      let seniorComments = '';
      if (submissionData.status_history && submissionData.status_history.length > 0) {
        // Find the most recent "sent_back_by_senior" status entry
        const sentBackEntry = submissionData.status_history.find(
          h => h.current_status === 'sent_back_by_senior' || h.status === 'sent_back_by_senior'
        );
        if (sentBackEntry && sentBackEntry.comments) {
          seniorComments = sentBackEntry.comments;
        }
      }

      // Add comments to submission data
      submissionData.senior_inspector_comments = seniorComments;

      setSubmission(submissionData);
      setSelectedClientId(submissionData.client_id);
      setInspectionDate(submissionData.inspection_date);

      // Fetch template fields
      const templateResponse = await formService.getTemplateFields(submissionData.template_id);

      if (templateResponse.success) {
        setTemplate(templateResponse.template);
        setFields(templateResponse.fields);

        // Initialize form data with existing submission data
        const initialData = {};

        // Backend returns form_data as an object/dictionary, not array
        // Structure: { field_id: { field: {...}, value: "..." }, ... }
        const formDataObj = submissionData.form_data;

        console.log('Received form_data type:', typeof formDataObj);
        console.log('Received form_data structure:', formDataObj);
        console.log('Is form_data an object?', formDataObj && typeof formDataObj === 'object');
        console.log('Is form_data an array?', Array.isArray(formDataObj));

        // Handle both object and array formats
        if (formDataObj) {
          if (Array.isArray(formDataObj)) {
            // If it's an array (old format)
            console.log('Processing form_data as array');
            formDataObj.forEach((dataEntry) => {
              const field = templateResponse.fields.find(f => f.field_id === dataEntry.field_id);
              if (field && dataEntry.field_value !== undefined) {
                if (field.field_type === 'checkbox') {
                  initialData[dataEntry.field_id] = dataEntry.field_value ? dataEntry.field_value.split(',').map(v => v.trim()) : [];
                } else {
                  initialData[dataEntry.field_id] = dataEntry.field_value;
                }
              }
            });
          } else if (typeof formDataObj === 'object') {
            // If it's an object/dictionary (new format from backend)
            console.log('Processing form_data as object');
            Object.keys(formDataObj).forEach((fieldIdStr) => {
              const fieldId = parseInt(fieldIdStr);
              const dataEntry = formDataObj[fieldIdStr];
              const field = templateResponse.fields.find(f => f.field_id === fieldId);

              console.log(`Processing field ${fieldId}:`, dataEntry);

              if (field && dataEntry) {
                const fieldValue = dataEntry.value || '';

                if (field.field_type === 'checkbox') {
                  // Parse comma-separated values into array
                  initialData[fieldId] = fieldValue ? fieldValue.split(',').map(v => v.trim()) : [];
                } else {
                  initialData[fieldId] = fieldValue;
                }

                console.log(`  Set initialData[${fieldId}] =`, initialData[fieldId]);
              }
            });
          }
        }

        console.log('Final initialData:', initialData);
        setFormData(initialData);
        console.log('Form data state updated');
      }

      if (clientsResponse.success) {
        setClients(clientsResponse.clients);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error loading submission:', err);
      console.error('Error stack:', err.stack);
      // Provide more user-friendly error message
      let errorMessage = 'Failed to load submission data';
      if (err.message) {
        if (err.message.includes('forEach')) {
          errorMessage = 'Error loading form data structure. Please contact support.';
        } else {
          errorMessage = err.message;
        }
      }
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleInputChange = (fieldId, value, fieldType) => {
    if (fieldType === 'checkbox') {
      const currentValues = formData[fieldId] || [];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      setFormData({ ...formData, [fieldId]: newValues });
    } else {
      setFormData({ ...formData, [fieldId]: value });

      // Special handling: Clear danger date field if immediate danger is not "yes"
      const field = fields.find(f => f.field_id === fieldId);
      if (field) {
        const label = (field.field_label || '').toLowerCase();
        if (label.includes('immediate') && label.includes('danger') && !label.includes('date')) {
          if (value.toLowerCase() !== 'yes') {
            const dangerDateField = fields.find(f => {
              const dateLabel = (f.field_label || '').toLowerCase();
              return dateLabel.includes('danger') && dateLabel.includes('date');
            });
            if (dangerDateField) {
              setFormData(prev => ({ ...prev, [dangerDateField.field_id]: '' }));
            }
          }
        }
      }

      // Default "summary" to the option at the same index as the selected "type_of_inspection"
      // option — the two dropdowns' options are authored in matching order. Inspector can still
      // override the summary afterward.
      if (field && field.field_name === 'type_of_inspection') {
        const summaryField = fields.find(f => f.field_name === 'summary');
        if (summaryField) {
          const typeOptions = typeof field.field_options === 'string'
            ? JSON.parse(field.field_options)
            : (field.field_options || []);
          const summaryOptions = typeof summaryField.field_options === 'string'
            ? JSON.parse(summaryField.field_options)
            : (summaryField.field_options || []);
          const selectedIndex = typeOptions.findIndex(opt => (typeof opt === 'object' ? opt.value : opt) === value);
          if (selectedIndex !== -1 && summaryOptions[selectedIndex]) {
            const summaryOption = summaryOptions[selectedIndex];
            setFormData(prev => ({ ...prev, [summaryField.field_id]: typeof summaryOption === 'object' ? summaryOption.value : summaryOption }));
            setSelectDisplayIndex(prev => ({ ...prev, [summaryField.field_id]: selectedIndex }));
          }
        }
      }
    }

    // Clear error for this field
    if (errors[fieldId]) {
      setErrors({ ...errors, [fieldId]: '' });
    }
  };

  const validateField = (field, value) => {
    if (field.is_required) {
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return `${field.field_label} is required`;
      }
    }

    if (field.field_type === 'number' && value) {
      const numValue = parseFloat(value);
      if (field.min_value !== null && numValue < field.min_value) {
        return `${field.field_label} must be at least ${field.min_value}`;
      }
      if (field.max_value !== null && numValue > field.max_value) {
        return `${field.field_label} must be at most ${field.max_value}`;
      }
    }

    if ((field.field_type === 'text' || field.field_type === 'textarea') && value) {
      if (field.min_length && value.length < field.min_length) {
        return `${field.field_label} must be at least ${field.min_length} characters`;
      }
      if (field.max_length && value.length > field.max_length) {
        return `${field.field_label} must be at most ${field.max_length} characters`;
      }
    }

    if (field.validation_regex && value) {
      const regex = new RegExp(field.validation_regex);
      if (!regex.test(value)) {
        return field.validation_message || `${field.field_label} format is invalid`;
      }
    }

    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all required fields
    const newErrors = {};
    fields.forEach((field) => {
      if (field.field_type !== 'static_text') {
        const value = formData[field.field_id];
        const errorMsg = validateField(field, value);
        if (errorMsg) {
          newErrors[field.field_id] = errorMsg;
        }
      }
    });

    // Validate client selection
    if (!selectedClientId) {
      setError('Please select a client');
      return;
    }

    // Validate inspection date
    if (!inspectionDate) {
      setError('Please select an inspection date');
      return;
    }

    // Check for any validation errors
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setError('Please fix the validation errors before submitting');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      setSuccess('');

      // Prepare submission data
      const submissionData = {
        submission_id: submissionId,
        client_id: selectedClientId,
        inspection_date: inspectionDate,
        form_data: []
      };

      // Add all field values
      fields.forEach((field) => {
        if (field.field_type !== 'static_text') {
          let value = formData[field.field_id];

          // Convert checkbox arrays to comma-separated string
          if (field.field_type === 'checkbox' && Array.isArray(value)) {
            value = value.join(', ');
          }

          submissionData.form_data.push({
            field_id: field.field_id,
            field_value: value || ''
          });
        }
      });

      console.log('Updating submission with data:', submissionData);

      // Update submission
      const response = await formService.updateSubmission(submissionData);

      if (response.success) {
        setSuccess(`Form ${submission.form_number} updated successfully!`);

        // Redirect to dashboard after 2 seconds
        setTimeout(() => {
          navigate('/dashboard/jr_inspector');
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to update form');
      }
    } catch (err) {
      console.error('Error updating form:', err);
      setError(err.message || 'Failed to update form. Please try again.');
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    // Signature file fields are never uploaded through the form — the certificate PDF fills
    // inspector_signature/reviewer_signature automatically from the inspector/reviewer name
    // (see pdf_template_utils.py). witnessed_by_signature has no such auto-fill (external
    // client/TPI signatory) and is left blank on the certificate for physical signing.
    if (['inspector_signature', 'reviewer_signature', 'witnessed_by_signature'].includes(field.field_name)) return null;

    if (field.field_type === 'static_text') {
      return (
        <div key={field.field_id} className="mb-3">
          <div className="text-muted" style={{ whiteSpace: 'pre-wrap' }}>
            {field.field_label}
          </div>
        </div>
      );
    }

    if (field.field_type === 'file') {
      return (
        <PhotoFieldUpload
          key={field.field_id}
          field={field}
          value={formData[field.field_id]}
          onChange={(filename) => handleInputChange(field.field_id, filename, field.field_type)}
        />
      );
    }

    if (field.field_type === 'table_rows') {
      return (
        <DynamicRowsField
          key={field.field_id}
          field={field}
          value={formData[field.field_id]}
          onChange={(jsonValue) => handleInputChange(field.field_id, jsonValue, field.field_type)}
        />
      );
    }

    const fieldValue = formData[field.field_id] || '';
    const fieldError = errors[field.field_id];

    // Check if this is the danger date field
    const isDangerDateField = (() => {
      const label = (field.field_label || '').toLowerCase();
      return label.includes('danger') && label.includes('date');
    })();

    // Check if danger date field should be shown
    const shouldShowDangerDate = (() => {
      if (!isDangerDateField) return true;

      const dangerField = fields.find(f => {
        const label = (f.field_label || '').toLowerCase();
        return label.includes('immediate') && label.includes('danger') && !label.includes('date');
      });

      if (!dangerField) return true;

      const dangerValue = (formData[dangerField.field_id] || '').toLowerCase();
      return dangerValue === 'yes';
    })();

    if (!shouldShowDangerDate) {
      return null;
    }

    // Render standards/reference fields as editable dropdowns (combobox)
    const dropdownConfig = DYNAMIC_DROPDOWN_FIELDS[field.field_name];
    if (field.field_type === 'text' && dropdownConfig) {
      const options = dynamicOptions[dropdownConfig.dataKey] || [];
      const datalistId = `datalist-${field.field_id}`;
      return (
        <Form.Group key={field.field_id} className="mb-3">
          <Form.Label>
            {field.field_label}
            {field.is_required && <span className="text-danger"> *</span>}
          </Form.Label>
          <Form.Control
            type="text"
            list={datalistId}
            value={fieldValue}
            onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
            placeholder={`Select or type ${field.field_label}...`}
            isInvalid={!!fieldError}
          />
          <datalist id={datalistId}>
            {options.map((option) => (
              <option key={option.id} value={option[dropdownConfig.valueField]} />
            ))}
          </datalist>
          <Form.Text className="text-muted d-block">
            <i className="fas fa-info-circle me-1"></i>
            Select from list or type/edit your own value
          </Form.Text>
          {fieldError && <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{fieldError}</Form.Control.Feedback>}
        </Form.Group>
      );
    }

    return (
      <Form.Group key={field.field_id} className="mb-3">
        <Form.Label>
          {field.field_label}
          {field.is_required && <span className="text-danger"> *</span>}
        </Form.Label>

        {field.field_type === 'text' && (
          <Form.Control
            type="text"
            value={fieldValue}
            onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
            placeholder={field.help_text || `Enter ${field.field_label}`}
            isInvalid={!!fieldError}
          />
        )}

        {field.field_type === 'email' && (
          <Form.Control
            type="email"
            value={fieldValue}
            onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
            placeholder={field.help_text || 'Enter email'}
            isInvalid={!!fieldError}
          />
        )}

        {field.field_type === 'number' && (
          <Form.Control
            type="number"
            value={fieldValue}
            onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
            placeholder={field.help_text || 'Enter number'}
            min={field.min_value}
            max={field.max_value}
            isInvalid={!!fieldError}
          />
        )}

        {field.field_type === 'date' && (
          <Form.Control
            type="date"
            value={fieldValue}
            onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
            isInvalid={!!fieldError}
          />
        )}

        {field.field_type === 'url' && (
          <Form.Control
            type="url"
            value={fieldValue}
            onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
            placeholder={field.help_text || 'Enter URL'}
            isInvalid={!!fieldError}
          />
        )}

        {field.field_type === 'textarea' && (
          <Form.Control
            as="textarea"
            rows={4}
            value={fieldValue}
            onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
            placeholder={field.help_text || `Enter ${field.field_label}`}
            isInvalid={!!fieldError}
          />
        )}

        {field.field_type === 'select' && (() => {
          let options = [];
          try {
            options = typeof field.field_options === 'string'
              ? JSON.parse(field.field_options)
              : (field.field_options || []);
          } catch (e) {
            options = [];
          }
          // Options are keyed by index (not text value) so fields with duplicate-text options
          // (e.g. "summary") visually highlight the exact option chosen, not just the first
          // option that happens to share the same text.
          const currentSelectIndex = selectDisplayIndex[field.field_id] !== undefined
            ? selectDisplayIndex[field.field_id]
            : options.findIndex(option => (typeof option === 'object' ? option.value : option) === fieldValue);
          return (
            <Form.Select
              value={currentSelectIndex >= 0 ? currentSelectIndex : ''}
              onChange={(e) => {
                const idx = e.target.value === '' ? -1 : parseInt(e.target.value, 10);
                const selectedOption = options[idx];
                const optionValue = selectedOption ? (typeof selectedOption === 'object' ? selectedOption.value : selectedOption) : '';
                setSelectDisplayIndex(prev => ({ ...prev, [field.field_id]: idx }));
                handleInputChange(field.field_id, optionValue, field.field_type);
              }}
              isInvalid={!!fieldError}
            >
              <option value="">-- Select --</option>
              {options.map((option, idx) => {
                const optionLabel = typeof option === 'object' ? option.label : option;
                return <option key={idx} value={idx}>{optionLabel}</option>;
              })}
            </Form.Select>
          );
        })()}

        {field.field_type === 'radio' && (
          <div>
            {(() => {
              try {
                const options = typeof field.field_options === 'string'
                  ? JSON.parse(field.field_options)
                  : (field.field_options || []);
                return options.map((option, idx) => {
                  const optionValue = typeof option === 'object' ? option.value : option;
                  const optionLabel = typeof option === 'object' ? option.label : option;
                  return (
                    <Form.Check
                      key={idx}
                      type="radio"
                      id={`${field.field_id}-${idx}`}
                      label={optionLabel}
                      value={optionValue}
                      checked={fieldValue === optionValue}
                      onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
                      isInvalid={!!fieldError}
                    />
                  );
                });
              } catch (e) {
                return <div className="text-danger">Error loading options</div>;
              }
            })()}
          </div>
        )}

        {field.field_type === 'checkbox' && (
          <div>
            {(() => {
              try {
                const options = typeof field.field_options === 'string'
                  ? JSON.parse(field.field_options)
                  : (field.field_options || []);
                const currentValues = Array.isArray(fieldValue) ? fieldValue : [];

                return options.map((option, idx) => {
                  const optionValue = typeof option === 'object' ? option.value : option;
                  const optionLabel = typeof option === 'object' ? option.label : option;
                  return (
                    <Form.Check
                      key={idx}
                      type="checkbox"
                      id={`${field.field_id}-${idx}`}
                      label={optionLabel}
                      value={optionValue}
                      checked={currentValues.includes(optionValue)}
                      onChange={(e) => handleInputChange(field.field_id, e.target.value, field.field_type)}
                      isInvalid={!!fieldError}
                    />
                  );
                });
              } catch (e) {
                return <div className="text-danger">Error loading options</div>;
              }
            })()}
          </div>
        )}

        {field.help_text && !fieldError && (
          <Form.Text className="text-muted">{field.help_text}</Form.Text>
        )}

        {fieldError && <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>{fieldError}</Form.Control.Feedback>}
      </Form.Group>
    );
  };

  const groupFieldsBySection = () => {
    const sections = {};
    fields.forEach((field) => {
      const section = field.field_section || 'General';
      if (!sections[section]) {
        sections[section] = [];
      }
      sections[section].push(field);
    });

    // Sort fields within each section by field_order
    Object.keys(sections).forEach((section) => {
      sections[section].sort((a, b) => (a.field_order || 0) - (b.field_order || 0));
    });

    return sections;
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Container className="mt-5 text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3">Loading form data...</p>
        </Container>
      </>
    );
  }

  if (error && !submission) {
    return (
      <>
        <Navbar />
        <Container className="mt-5">
          <Alert variant="danger">{error}</Alert>
          <Button onClick={() => navigate('/dashboard/jr_inspector')}>Back to Dashboard</Button>
        </Container>
      </>
    );
  }

  const sections = groupFieldsBySection();

  return (
    <>
      <Navbar />
      <Container className="mt-4 mb-5">
        <Row className="mb-4">
          <Col>
            <h2>Edit Inspection Form</h2>
            <div className="d-flex align-items-center gap-2 mb-3">
              <Badge bg="info">{submission?.form_type_name}</Badge>
              <Badge bg="success">{submission?.equipment_type_name}</Badge>
              <Badge bg="warning" text="dark">Form Number: {submission?.form_number}</Badge>
            </div>
            {submission?.senior_inspector_comments && (
              <Alert variant="warning">
                <strong>Comments from Reviewer:</strong>
                <p className="mb-0 mt-2">{submission.senior_inspector_comments}</p>
              </Alert>
            )}
          </Col>
        </Row>

        {success && <Alert variant="success">{success}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Basic Information</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>
                  Client <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={selectedClientId}
                  onChange={(e) => setSelectedClientId(e.target.value)}
                  required
                >
                  <option value="">-- Select Client --</option>
                  {clients.map((client) => (
                    <option key={client.client_id} value={client.client_id}>
                      {client.client_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Inspection Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  value={inspectionDate}
                  onChange={(e) => setInspectionDate(e.target.value)}
                  required
                />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Dynamic Form Sections */}
          {Object.keys(sections).map((sectionName) => (
            <Card key={sectionName} className="mb-4">
              <Card.Header>
                <h5 className="mb-0">{sectionName}</h5>
              </Card.Header>
              <Card.Body>
                {sections[sectionName].map((field) => renderField(field))}
              </Card.Body>
            </Card>
          ))}

          {/* Action Buttons */}
          <div className="d-flex gap-2 justify-content-end">
            <Button
              variant="secondary"
              onClick={() => navigate('/dashboard/jr_inspector')}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Updating...
                </>
              ) : (
                'Update Form'
              )}
            </Button>
          </div>
        </Form>
      </Container>
    </>
  );
};

export default EditSubmission;
